import { useState, useMemo } from 'react';
import { Plus, Edit3, Trash2, Copy, X, Package, Calculator, Star, ToggleLeft, ToggleRight, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { useCatalogStore, genId } from '../store/catalogStore';
import { toast } from '../components/Toast';
import type { CCTVPackage, PackageItem } from '../types/catalog';

const PKG_TYPES = [
  { value: '4cam', label: '4 Camera Package' },
  { value: '8cam', label: '8 Camera Package' },
  { value: '16cam', label: '16 Camera Package' },
  { value: 'custom', label: 'Custom Package' },
] as const;

const ITEM_TYPES = [
  'camera', 'recorder', 'storage', 'accessory', 'cable', 'installation', 'other'
] as const;

function formatLKR(v: number) {
  return v > 0 ? `Rs. ${v.toLocaleString('en-LK')}` : '—';
}

function calcPackageCost(items: PackageItem[], labor = 0, installation = 0, discount = 0) {
  const itemsTotal = items.reduce((sum, i) => sum + (i.quantity * (i.unitPrice ?? 0)), 0);
  return Math.max(0, itemsTotal + labor + installation - discount);
}

export default function PackagesPage() {
  const { brands, categories, catalogProducts, packages, addPackage, updatePackage, deletePackage, duplicatePackage, addPackageItem, updatePackageItem, removePackageItem } = useCatalogStore();

  const [editingPkg, setEditingPkg] = useState<CCTVPackage | null>(null);
  const [showNewPkg, setShowNewPkg] = useState(false);

  // New package form
  const [newPkgForm, setNewPkgForm] = useState({
    title: '', description: '', brandId: '', packageType: '4cam' as CCTVPackage['packageType'],
    cameraQty: 4, sellingPrice: 0, laborCost: 0, installationCost: 0, discount: 0,
    isEnabled: true, isFeatured: false, isMostPopular: false,
    inclusions: ['Professional Installation & Configuration', 'Mobile Phone Live View Setup', 'Product Warranty', 'K-Tec Service Warranty'],
    image: '',
  });

  // Item addition form (for editing package)
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemForm, setItemForm] = useState<Partial<PackageItem>>({
    itemType: 'camera', quantity: 1, unitPrice: 0, notes: '', productId: '', customName: '',
  });

  const sortedPackages = useMemo(() => [...packages].sort((a, b) => +b.isFeatured - +a.isFeatured), [packages]);

  const handleCreatePackage = () => {
    if (!newPkgForm.title.trim()) { toast.error('Validation', 'Package title is required.'); return; }
    const now = new Date().toISOString();
    const pkg: CCTVPackage = {
      id: genId(),
      title: newPkgForm.title,
      slug: newPkgForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
      description: newPkgForm.description,
      brandId: newPkgForm.brandId || undefined,
      packageType: newPkgForm.packageType,
      cameraQty: newPkgForm.cameraQty,
      items: [],
      sellingPrice: newPkgForm.sellingPrice,
      laborCost: newPkgForm.laborCost,
      installationCost: newPkgForm.installationCost,
      discount: newPkgForm.discount,
      isEnabled: true,
      isFeatured: false,
      isMostPopular: false,
      inclusions: newPkgForm.inclusions.filter(Boolean),
      createdAt: now,
      updatedAt: now,
    };
    addPackage(pkg);
    setEditingPkg(pkg);
    setShowNewPkg(false);
    toast.success('Package Created', `"${pkg.title}" created. Now add products.`);
  };

  const handleSavePackageField = (id: string, field: string, value: any) => {
    updatePackage(id, { [field]: value });
    setEditingPkg((p) => p ? { ...p, [field]: value } : null);
  };

  const handleAddItem = () => {
    if (!editingPkg) return;
    const item: PackageItem = {
      id: genId(),
      packageId: editingPkg.id,
      productId: itemForm.productId || undefined,
      customName: itemForm.customName,
      quantity: itemForm.quantity ?? 1,
      unitPrice: itemForm.unitPrice,
      notes: itemForm.notes,
      itemType: (itemForm.itemType as PackageItem['itemType']) ?? 'other',
      displayOrder: editingPkg.items.length + 1,
    };
    // Auto-fill unit price from product
    if (item.productId) {
      const prod = catalogProducts.find((p) => p.id === item.productId);
      if (prod && !item.unitPrice) item.unitPrice = prod.costPrice ?? prod.regularPrice;
      if (!item.customName) item.customName = prod?.name;
    }
    addPackageItem(editingPkg.id, item);
    setEditingPkg((p) => p ? { ...p, items: [...p.items, item] } : null);
    setItemForm({ itemType: 'camera', quantity: 1, unitPrice: 0, notes: '', productId: '', customName: '' });
    setShowItemForm(false);
    toast.success('Item Added');
  };

  const handleRemoveItem = (itemId: string) => {
    if (!editingPkg) return;
    removePackageItem(editingPkg.id, itemId);
    setEditingPkg((p) => p ? { ...p, items: p.items.filter((i) => i.id !== itemId) } : null);
  };

  const handleDeletePackage = (pkg: CCTVPackage) => {
    if (!confirm(`Delete package "${pkg.title}"?`)) return;
    deletePackage(pkg.id);
    if (editingPkg?.id === pkg.id) setEditingPkg(null);
    toast.success('Package Deleted');
  };

  const pkgCost = (pkg: CCTVPackage) => calcPackageCost(pkg.items, pkg.laborCost, pkg.installationCost, pkg.discount);
  const pkgProfit = (pkg: CCTVPackage) => pkg.sellingPrice - pkgCost(pkg);

  const brandName = (id?: string) => !id ? 'Mixed / Custom' : (brands.find((b) => b.id === id)?.name ?? '—');
  const productName = (id?: string) => !id ? '' : (catalogProducts.find((p) => p.id === id)?.name ?? '');

  // ── Inclusion editor
  const [inclusionInput, setInclusionInput] = useState('');

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">CCTV Packages</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{packages.length} packages configured</p>
        </div>
        <button
          onClick={() => setShowNewPkg(true)}
          className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Package
        </button>
      </div>

      {editingPkg ? (
        /* ── Package Builder ────────────────────────────────────────────────── */
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setEditingPkg(null)} className="h-9 px-4 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">← Back</button>
            <h2 className="text-[18px] font-semibold text-gray-900">{editingPkg.title}</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Package Info */}
            <div className="lg:col-span-2 space-y-4">
              {/* Info Card */}
              <div className="bg-white rounded-2xl border border-gray-200/60 p-5 space-y-4">
                <h3 className="text-[14px] font-semibold text-gray-800">Package Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Package Title</label>
                    <input
                      defaultValue={editingPkg.title}
                      onBlur={(e) => handleSavePackageField(editingPkg.id, 'title', e.target.value)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Package Type</label>
                    <select value={editingPkg.packageType} onChange={(e) => handleSavePackageField(editingPkg.id, 'packageType', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                      {PKG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Brand</label>
                    <select value={editingPkg.brandId ?? ''} onChange={(e) => handleSavePackageField(editingPkg.id, 'brandId', e.target.value || undefined)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                      <option value="">Mixed / Custom</option>
                      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Camera Quantity</label>
                    <input type="number" defaultValue={editingPkg.cameraQty} onBlur={(e) => handleSavePackageField(editingPkg.id, 'cameraQty', +e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Description</label>
                    <textarea defaultValue={editingPkg.description} onBlur={(e) => handleSavePackageField(editingPkg.id, 'description', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Package Image URL</label>
                    <input defaultValue={editingPkg.image} onBlur={(e) => handleSavePackageField(editingPkg.id, 'image', e.target.value)} placeholder="https://..." className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 pt-1">
                  {[
                    { key: 'isEnabled', label: 'Enabled' },
                    { key: 'isFeatured', label: 'Featured' },
                    { key: 'isMostPopular', label: 'Most Popular' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={(editingPkg as any)[key]} onChange={(e) => handleSavePackageField(editingPkg.id, key, e.target.checked)} className="w-4 h-4 accent-[#7FA86A]" />
                      <span className="text-[13px] text-gray-600">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Items Table */}
              <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold text-gray-800">Package Items</h3>
                  <button onClick={() => setShowItemForm(true)} className="h-8 px-4 rounded-xl bg-gray-900 text-white text-[12px] font-medium flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Add Item
                  </button>
                </div>

                {editingPkg.items.length === 0 ? (
                  <div className="py-10 text-center text-gray-400 text-[13px]">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No items yet. Click "Add Item" to build this package.
                  </div>
                ) : (
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Item</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Type</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Qty</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Unit Price</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Total</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingPkg.items.map((item) => {
                        const linkedName = item.productId ? productName(item.productId) : '';
                        const displayName = linkedName || item.customName || '—';
                        return (
                          <tr key={item.id} className="border-b border-gray-100 last:border-0">
                            <td className="px-4 py-2.5">
                              <p className="font-medium text-gray-800">{displayName}</p>
                              {item.notes && <p className="text-gray-400 text-[11px]">{item.notes}</p>}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] capitalize">{item.itemType}</span>
                            </td>
                            <td className="px-4 py-2.5 text-right">{item.quantity}</td>
                            <td className="px-4 py-2.5 text-right">{item.unitPrice ? formatLKR(item.unitPrice) : '—'}</td>
                            <td className="px-4 py-2.5 text-right font-medium">{item.unitPrice ? formatLKR(item.quantity * item.unitPrice) : '—'}</td>
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => handleRemoveItem(item.id)} className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center ml-auto" aria-label="Remove">
                                <X className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Add Item Form */}
                {showItemForm && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50/50 space-y-3">
                    <h4 className="text-[13px] font-semibold text-gray-700">Add Item</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Item Type</label>
                        <select value={itemForm.itemType} onChange={(e) => setItemForm((f) => ({ ...f, itemType: e.target.value as any }))} className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          {ITEM_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Link to Product (optional)</label>
                        <select value={itemForm.productId} onChange={(e) => {
                          const prod = catalogProducts.find(p => p.id === e.target.value);
                          setItemForm((f) => ({
                            ...f,
                            productId: e.target.value,
                            customName: prod?.name ?? f.customName,
                            unitPrice: prod ? (prod.costPrice ?? prod.regularPrice) : f.unitPrice,
                          }));
                        }} className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          <option value="">— Custom Item —</option>
                          {catalogProducts.map((p) => <option key={p.id} value={p.id}>{p.name} ({brands.find(b=>b.id===p.brandId)?.name})</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Item Name / Description</label>
                        <input value={itemForm.customName} onChange={(e) => setItemForm((f) => ({ ...f, customName: e.target.value }))} placeholder="e.g. 4× Dahua 5MP Dome Camera" className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Quantity</label>
                        <input type="number" min={1} value={itemForm.quantity} onChange={(e) => setItemForm((f) => ({ ...f, quantity: +e.target.value }))} className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Unit Cost Price (Rs.)</label>
                        <input type="number" value={itemForm.unitPrice} onChange={(e) => setItemForm((f) => ({ ...f, unitPrice: +e.target.value }))} className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Notes (optional)</label>
                        <input value={itemForm.notes} onChange={(e) => setItemForm((f) => ({ ...f, notes: e.target.value }))} className="w-full h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowItemForm(false)} className="h-9 px-4 rounded-xl bg-gray-100 text-gray-600 text-[13px]">Cancel</button>
                      <button onClick={handleAddItem} className="h-9 px-4 rounded-xl bg-[#7FA86A] text-white text-[13px] font-medium">Add Item</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Inclusions */}
              <div className="bg-white rounded-2xl border border-gray-200/60 p-5">
                <h3 className="text-[14px] font-semibold text-gray-800 mb-3">Customer-Facing Inclusions</h3>
                <div className="space-y-2 mb-3">
                  {editingPkg.inclusions.map((inc, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#7FA86A] shrink-0" />
                      <span className="flex-1 text-[13px] text-gray-700">{inc}</span>
                      <button onClick={() => {
                        const newInc = editingPkg.inclusions.filter((_, i) => i !== idx);
                        handleSavePackageField(editingPkg.id, 'inclusions', newInc);
                      }} className="w-6 h-6 rounded hover:bg-red-50 flex items-center justify-center" aria-label="Remove inclusion">
                        <X className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={inclusionInput} onChange={(e) => setInclusionInput(e.target.value)} onKeyDown={(e) => {
                    if (e.key === 'Enter' && inclusionInput.trim()) {
                      handleSavePackageField(editingPkg.id, 'inclusions', [...editingPkg.inclusions, inclusionInput.trim()]);
                      setInclusionInput('');
                    }
                  }} placeholder="Add inclusion item (press Enter)..." className="flex-1 h-9 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  <button onClick={() => {
                    if (inclusionInput.trim()) {
                      handleSavePackageField(editingPkg.id, 'inclusions', [...editingPkg.inclusions, inclusionInput.trim()]);
                      setInclusionInput('');
                    }
                  }} className="h-9 px-4 rounded-xl bg-gray-900 text-white text-[13px]">Add</button>
                </div>
              </div>
            </div>

            {/* Right: Pricing Calculator */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200/60 p-5 space-y-4 sticky top-6">
                <h3 className="text-[14px] font-semibold text-gray-800 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-[#7FA86A]" /> Pricing Calculator
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Labour Cost (Rs.)</label>
                    <input type="number" defaultValue={editingPkg.laborCost} onBlur={(e) => handleSavePackageField(editingPkg.id, 'laborCost', +e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Installation Cost (Rs.)</label>
                    <input type="number" defaultValue={editingPkg.installationCost} onBlur={(e) => handleSavePackageField(editingPkg.id, 'installationCost', +e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1">Discount (Rs.)</label>
                    <input type="number" defaultValue={editingPkg.discount} onBlur={(e) => handleSavePackageField(editingPkg.id, 'discount', +e.target.value)} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                  </div>
                </div>
                {/* Cost Summary */}
                <div className="border-t border-gray-100 pt-4 space-y-2 text-[13px]">
                  <div className="flex justify-between text-gray-500">
                    <span>Items Cost</span>
                    <span>{formatLKR(editingPkg.items.reduce((s, i) => s + i.quantity * (i.unitPrice ?? 0), 0))}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Labour</span>
                    <span>{formatLKR(editingPkg.laborCost ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Installation</span>
                    <span>{formatLKR(editingPkg.installationCost ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Discount</span>
                    <span>−{formatLKR(editingPkg.discount ?? 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-100 pt-2">
                    <span>Total Cost</span>
                    <span>{formatLKR(pkgCost(editingPkg))}</span>
                  </div>
                </div>
                {/* Selling Price */}
                <div>
                  <label className="block text-[12px] font-semibold text-gray-700 mb-1">Selling Price (Rs.) *</label>
                  <input type="number" defaultValue={editingPkg.sellingPrice} onBlur={(e) => handleSavePackageField(editingPkg.id, 'sellingPrice', +e.target.value)} className="w-full h-10 px-3 rounded-xl border-2 border-[#7FA86A] text-[14px] font-semibold focus:outline-none" />
                </div>
                {/* Profit */}
                {editingPkg.sellingPrice > 0 && (
                  <div className={`px-4 py-3 rounded-xl text-[13px] font-semibold ${pkgProfit(editingPkg) >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                    Estimated Profit: {formatLKR(pkgProfit(editingPkg))}
                    {pkgCost(editingPkg) > 0 && (
                      <span className="font-normal text-[11px] ml-1">
                        ({((pkgProfit(editingPkg) / pkgCost(editingPkg)) * 100).toFixed(1)}% margin)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── Package List ─────────────────────────────────────────────────────── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sortedPackages.map((pkg) => (
            <div key={pkg.id} className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden hover:shadow-md transition-shadow">
              {/* Package Image */}
              {pkg.image ? (
                <img src={pkg.image} alt={pkg.title} className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <Package className="w-10 h-10 text-[#7FA86A] opacity-60" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">{pkg.title}</p>
                    <p className="text-[12px] text-gray-400 mt-0.5">{brandName(pkg.brandId)} · {pkg.cameraQty > 0 ? `${pkg.cameraQty} Cameras` : 'Custom'}</p>
                  </div>
                  <div className="flex gap-1">
                    {pkg.isMostPopular && <span className="bg-[#7FA86A] text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">Popular</span>}
                    {pkg.isFeatured && <span className="bg-amber-400 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">Featured</span>}
                  </div>
                </div>
                <p className="text-[12px] text-gray-500 mb-3 line-clamp-2">{pkg.description}</p>
                <div className="flex items-center justify-between text-[13px] mb-4">
                  <div>
                    <span className="text-[20px] font-bold text-[#7FA86A]">{pkg.sellingPrice > 0 ? formatLKR(pkg.sellingPrice) : 'Price TBD'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${pkg.isEnabled ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                    <span className="text-[11px] text-gray-400">{pkg.isEnabled ? 'Active' : 'Disabled'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingPkg(pkg)} className="flex-1 h-9 rounded-xl bg-gray-900 text-white text-[12px] font-medium hover:bg-gray-800 flex items-center justify-center gap-1">
                    <Edit3 className="w-3.5 h-3.5" /> Edit Package
                  </button>
                  <button onClick={() => duplicatePackage(pkg.id)} className="w-9 h-9 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center" aria-label="Duplicate">
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeletePackage(pkg)} className="w-9 h-9 rounded-xl hover:bg-red-50 flex items-center justify-center" aria-label="Delete">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── New Package Modal ─────────────────────────────────────────────── */}
      {showNewPkg && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-[17px] font-semibold text-gray-900">Create New Package</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Package Title *</label>
                <input value={newPkgForm.title} onChange={(e) => setNewPkgForm((f) => ({ ...f, title: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 4 Camera Dahua Package" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-medium text-gray-500 mb-1">Type</label>
                  <select value={newPkgForm.packageType} onChange={(e) => setNewPkgForm((f) => ({ ...f, packageType: e.target.value as any }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                    {PKG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-gray-500 mb-1">Brand</label>
                  <select value={newPkgForm.brandId} onChange={(e) => setNewPkgForm((f) => ({ ...f, brandId: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                    <option value="">Mixed / Custom</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Description</label>
                <textarea value={newPkgForm.description} onChange={(e) => setNewPkgForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none" placeholder="Short package description..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setShowNewPkg(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleCreatePackage} className="h-10 px-5 rounded-xl bg-[#7FA86A] text-white text-[13px] font-medium hover:bg-[#658C53]">Create & Build</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
